/**
 * Workflow Recipes API Service
 *
 * Type-safe client for the recipe catalog and the per-tenant allowlist.
 * Types derived from schema-registry/workflow-recipes.openapi.yaml -- every
 * shape here is imported from the generated leaf, none re-declared.
 *
 * Scope: Aaim-operator (platform.aaim.com / platformAdmin). Recipe AUTHORING
 * and ALLOWLIST editing are platform-curation acts behind requirePlatformAdmin;
 * the FI admin's act is SELECTION from the allowlisted subset, which lives on
 * admin-portal. See .claude/rules/scope-orientation-c4-first.md.
 *
 * baseUrl is '' rather than '/api/v1' because this spec declares its paths
 * fully-qualified (`/api/v1/workflow-recipes/...`) -- the generated path keys
 * already carry the prefix, so prepending it again would produce
 * /api/v1/api/v1/... The sibling collateral-policy service uses '/api/v1'
 * because its spec's keys are unprefixed. Both are correct for their spec.
 */

import createClient from 'openapi-fetch';
import type {
  paths,
  components,
} from '@aaimplatform/institutional-types/generated/workflow-recipes';
import { authMiddleware } from '../client';

export type WorkflowRecipe = components['schemas']['WorkflowRecipe'];
export type WorkflowRecipeFamily = components['schemas']['WorkflowRecipeFamily'];
export type WorkflowRecipeListResponse = components['schemas']['WorkflowRecipeListResponse'];
export type RecipeStep = components['schemas']['RecipeStep'];
export type RecipeGate = components['schemas']['RecipeGate'];
export type RecipeActor = components['schemas']['RecipeActor'];
export type RecipeStatus = components['schemas']['RecipeStatus'];
export type RecipeValidationError = components['schemas']['RecipeValidationError'];
export type TenantRecipeAllowlist = components['schemas']['TenantRecipeAllowlist'];
export type TenantRecipeBinding = components['schemas']['TenantRecipeBinding'];
export type UpdateWorkflowRecipeDraftRequest =
  components['schemas']['UpdateWorkflowRecipeDraftRequest'];
export type CreateWorkflowRecipeVersionRequest =
  components['schemas']['CreateWorkflowRecipeVersionRequest'];

function createWorkflowRecipesClient() {
  const client = createClient<paths>({ baseUrl: '', credentials: 'include' });
  client.use(authMiddleware());
  return client;
}

const client = createWorkflowRecipesClient();

/**
 * A 422 from this surface carries `violations` -- the backend's V1-V9 rules.
 * They are surfaced verbatim rather than re-implemented in the SPA: the
 * validator is the authority, and a second copy in the UI would be a parallel
 * contract that drifts (the class .claude/rules/canon-fit-escalation.md names).
 */
// adhoc-type-skip: a discriminated result over the generated violation type; declares no new domain shape
export type RecipeMutationResult<T> =
  | { ok: true; data: T }
  | { ok: false; violations: RecipeValidationError[]; message: string };

function toFailure(error: unknown, fallback: string): {
  ok: false;
  violations: RecipeValidationError[];
  message: string;
} {
  const e = error as { message?: string; violations?: RecipeValidationError[] } | undefined;
  return {
    ok: false,
    violations: e?.violations ?? [],
    message: e?.message ?? fallback,
  };
}

export const workflowRecipesApi = {
  list: async (): Promise<WorkflowRecipeListResponse> => {
    const { data, error } = await client.GET('/api/v1/workflow-recipes', {});
    if (error) throw new Error('Failed to load the recipe catalog');
    return data ?? { recipes: [], total: 0 };
  },

  getFamily: async (recipeId: string): Promise<WorkflowRecipeFamily | null> => {
    const { data, error, response } = await client.GET('/api/v1/workflow-recipes/{recipeId}', {
      params: { path: { recipeId } },
    });
    if (response.status === 404) return null;
    if (error) throw new Error('Failed to load the recipe');
    return data ?? null;
  },

  getVersion: async (recipeId: string, version: number): Promise<WorkflowRecipe | null> => {
    const { data, error, response } = await client.GET(
      '/api/v1/workflow-recipes/{recipeId}/versions/{version}',
      { params: { path: { recipeId, version } } },
    );
    if (response.status === 404) return null;
    if (error) throw new Error('Failed to load the recipe version');
    return data ?? null;
  },

  /**
   * Mints a DRAFT: version 1 for a recipeId the catalog has never seen (a new
   * family), or max(existing version) + 1 for one it has -- the server decides
   * which from the id alone. This is the only path that can create a NEW
   * recipe family from the UI; every other mutation here operates on a
   * (recipeId, version) that already exists.
   */
  createVersion: async (
    recipeId: string,
    body: CreateWorkflowRecipeVersionRequest,
  ): Promise<RecipeMutationResult<WorkflowRecipe>> => {
    const { data, error } = await client.POST('/api/v1/workflow-recipes/{recipeId}/versions', {
      params: { path: { recipeId } },
      body,
    });
    if (error) return toFailure(error, 'The draft version could not be created');
    return { ok: true, data: data as WorkflowRecipe };
  },

  updateDraft: async (
    recipeId: string,
    version: number,
    body: UpdateWorkflowRecipeDraftRequest,
  ): Promise<RecipeMutationResult<WorkflowRecipe>> => {
    const { data, error } = await client.PATCH(
      '/api/v1/workflow-recipes/{recipeId}/versions/{version}',
      { params: { path: { recipeId, version } }, body },
    );
    if (error) return toFailure(error, 'The draft could not be saved');
    return { ok: true, data: data as WorkflowRecipe };
  },

  publish: async (recipeId: string, version: number): Promise<RecipeMutationResult<WorkflowRecipe>> => {
    const { data, error } = await client.POST(
      '/api/v1/workflow-recipes/{recipeId}/versions/{version}/publish',
      { params: { path: { recipeId, version } } },
    );
    if (error) return toFailure(error, 'The version could not be published');
    return { ok: true, data: data as WorkflowRecipe };
  },

  retire: async (recipeId: string, version: number): Promise<RecipeMutationResult<WorkflowRecipe>> => {
    const { data, error } = await client.POST(
      '/api/v1/workflow-recipes/{recipeId}/versions/{version}/retire',
      { params: { path: { recipeId, version } } },
    );
    if (error) return toFailure(error, 'The version could not be retired');
    return { ok: true, data: data as WorkflowRecipe };
  },

  getAllowlist: async (tenantId: string): Promise<TenantRecipeAllowlist | null> => {
    const { data, error, response } = await client.GET(
      '/api/v1/workflow-recipes/tenants/{tenantId}/allowlist',
      { params: { path: { tenantId } } },
    );
    if (response.status === 404) return null;
    if (error) throw new Error('Failed to load the tenant allowlist');
    return data ?? null;
  },

  putAllowlist: async (
    tenantId: string,
    recipeIds: string[],
  ): Promise<RecipeMutationResult<TenantRecipeAllowlist>> => {
    const { data, error } = await client.PUT(
      '/api/v1/workflow-recipes/tenants/{tenantId}/allowlist',
      { params: { path: { tenantId } }, body: { recipeIds } },
    );
    if (error) return toFailure(error, 'The allowlist could not be saved');
    return { ok: true, data: data as TenantRecipeAllowlist };
  },

  getBinding: async (tenantId: string): Promise<TenantRecipeBinding | null> => {
    const { data, error, response } = await client.GET(
      '/api/v1/workflow-recipes/tenants/{tenantId}/binding',
      { params: { path: { tenantId } } },
    );
    // 404 is a legitimate steady state: the tenant has selected nothing and
    // runs the platform default ordering. It is not an error.
    if (response.status === 404) return null;
    if (error) throw new Error('Failed to load the tenant binding');
    return data ?? null;
  },
};
